<template>
    <div class="responsive-image bg-gray-150" :class="{'responsive-image--zoomed': props.zoom}">
        <aspect-ratio-box :ratio="imageRatio">
            <div class="responsive-img" role="img" ref="image" :aria-label="props.alt" :style="imageStyle"/>
            <div v-if="props.gradient" class="absolute left-0 top-0 w-full h-full" :style="gradientStyle"/>
            <slot/>
        </aspect-ratio-box>
    </div>
</template>

<script setup lang="ts">
import type {ImageProps} from "../../types/image";
import {computed} from "vue";
import {useHead, useNuxtApp} from "#app";

const {$image} = useNuxtApp()

const props = withDefaults(defineProps<ImageProps>(), {
	center: true
});

const imageRatio = computed(() => {
	return props.ratio ?? (props.width && props.height ? props.width / props.height : 1);
});

const imageWidth = computed(() => {
	return props.width ?? (props.height ? Math.round(props.height * imageRatio.value) : null);
});

const imageHeight = computed(() => {
	return props.height ?? (props.width ? Math.round(props.width / imageRatio.value) : null);
});

const imageSrc = computed(() => {
	return $image.format(props.src, {
		width: imageWidth.value ? Math.round(imageWidth.value!) : null,
		height: imageHeight.value ? Math.round(imageHeight.value!) : null,
		fit: props.cover ? 'cover' : 'contain'
	});
});

const imageStyle = computed(() => {
	return {
		// Full width and height as the aspect ratio will hold the required space if the size is not forced
		width: '100%',
		height: '100%',
		backgroundRepeat: 'no-repeat',
		backgroundImage: `url(${imageSrc.value})`,
		backgroundSize: props.cover ? 'cover' : undefined,
		backgroundPosition: props.center ? 'enter center' : undefined,
		filter: props.blur ? `blur(${props.blur}px)` : undefined,
	};
});

const gradientStyle = computed(() => {
	return {
		backgroundImage: props.gradient ? `linear-gradient(${props.gradient})` : undefined,
	};
});

if (props.immediate) {
	useHead({
		link: [{
			rel: 'preload',
			as: 'image',
			href: imageSrc.value
		}]
	})
}
</script>

<style>
.responsive-image {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}
.responsive-image .responsive-img {
  border-radius: inherit;
  transition: opacity 0.8s, transform 0.4s;
  opacity: 1;
}
.responsive-image .responsive-img.loading {
  opacity: 0;
}
.responsive-image.responsive-image--zoomed .responsive-img {
  transform: scale(1.05);
}
.responsive-image.responsive-image--cover {
  width: 100%;
}
.responsive-image.responsive-image--cover .responsive-img {
  width: 100%;
  object-fit: cover;
}
</style>